import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Box,
  Button,
  Container,
  Divider,
  Drawer,
  IconButton,
  Link,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import PhoneOutlined from '@mui/icons-material/PhoneOutlined';
import EmailOutlined from '@mui/icons-material/EmailOutlined';
import BuildOutlined from '@mui/icons-material/BuildOutlined';

const LOGO_SRC = `${process.env.PUBLIC_URL || ''}/logo.png`;

const navigation = [
  { label: 'Properties', href: '/properties' },
  { label: 'Buy', href: '/buyers' },
  { label: 'Sell', href: '/sellers' },
  { label: 'Landlords', href: '/landlords' },
  { label: 'Tenants', href: '/tenants' },
  { label: 'About', href: '/about' },
];

export default function Header() {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 8);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => setMobileOpen(false), [location.pathname]);

  const isActive = (href) =>
    href === '/properties'
      ? location.pathname.startsWith('/properties') || location.pathname.startsWith('/property/')
      : location.pathname.startsWith(href);

  return (
    <Box component="header" sx={{ position: 'sticky', top: 0, zIndex: 1200 }}>
      <Box sx={{ display: { xs: 'none', md: 'block' }, bgcolor: '#10234b', color: '#fff' }}>
        <Container maxWidth="lg">
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ minHeight: 36 }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,.78)' }}>
              Independent estate agents serving Middlesbrough and Teesside
            </Typography>
            <Stack direction="row" spacing={2.5} alignItems="center">
              <Link href="tel:+441642243378" color="inherit" underline="none" sx={utilityLinkSx}>
                <PhoneOutlined sx={{ fontSize: 15 }} /> 01642 243 378
              </Link>
              <Link href="mailto:info@mrmlettings.co.uk" color="inherit" underline="none" sx={utilityLinkSx}>
                <EmailOutlined sx={{ fontSize: 15 }} /> info@mrmlettings.co.uk
              </Link>
              <Link component={RouterLink} to="/repairs" color="inherit" underline="none" sx={utilityLinkSx}>
                <BuildOutlined sx={{ fontSize: 15 }} /> Report a repair
              </Link>
            </Stack>
          </Stack>
        </Container>
      </Box>

      <AppBar
        position="static"
        color="inherit"
        elevation={0}
        sx={{
          bgcolor: scrolled ? 'rgba(255,255,255,.97)' : '#fff',
          borderBottom: '1px solid #e5eaf1',
          boxShadow: scrolled ? '0 8px 28px rgba(15,23,42,.08)' : 'none',
          backdropFilter: 'blur(12px)',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ minHeight: { xs: 68, md: 76 } }}>
            <Box
              component={RouterLink}
              to="/"
              aria-label="MRM Lettings home"
              sx={{ display: 'flex', alignItems: 'center', mr: { md: 4 }, flexGrow: { xs: 1, md: 0 } }}
            >
              <Box component="img" src={LOGO_SRC} alt="MRM Lettings" sx={{ width: 46, height: 46, objectFit: 'contain' }} />
            </Box>

            <Stack direction="row" spacing={0.25} sx={{ display: { xs: 'none', md: 'flex' }, flexGrow: 1 }}>
              {navigation.map((item) => (
                <Button
                  key={item.href}
                  component={RouterLink}
                  to={item.href}
                  color="inherit"
                  aria-current={isActive(item.href) ? 'page' : undefined}
                  sx={{
                    px: 1.4,
                    color: isActive(item.href) ? 'primary.main' : '#334155',
                    bgcolor: isActive(item.href) ? '#eef2ff' : 'transparent',
                    '&:hover': { bgcolor: '#f1f5f9', color: 'primary.main' },
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Stack>

            <Stack direction="row" spacing={1} sx={{ display: { xs: 'none', md: 'flex' } }}>
              <Button component={RouterLink} to="/contact" variant="text" color="inherit">
                Contact
              </Button>
              <Button component={RouterLink} to="/sellers/valuation" variant="contained">
                Book a valuation
              </Button>
            </Stack>

            <IconButton
              aria-label={mobileOpen ? 'Close navigation' : 'Open navigation'}
              onClick={() => setMobileOpen((open) => !open)}
              sx={{ display: { md: 'none' }, color: '#10234b' }}
            >
              {mobileOpen ? <CloseIcon /> : <MenuIcon />}
            </IconButton>
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        PaperProps={{ sx: { width: 'min(88vw, 360px)', bgcolor: '#fff' } }}
      >
        <Box sx={{ p: 2.5 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Box component="img" src={LOGO_SRC} alt="MRM Lettings" sx={{ width: 46, height: 46 }} />
            <IconButton aria-label="Close navigation" onClick={() => setMobileOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Stack>
          <Divider />
          <List sx={{ py: 1.5 }}>
            <ListItemButton component={RouterLink} to="/" selected={location.pathname === '/'}>
              <ListItemText primary="Home" primaryTypographyProps={{ fontWeight: 700 }} />
            </ListItemButton>
            {navigation.map((item) => (
              <ListItemButton key={item.href} component={RouterLink} to={item.href} selected={isActive(item.href)}>
                <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 700 }} />
              </ListItemButton>
            ))}
          </List>
          <Divider sx={{ mb: 2 }} />
          <Stack spacing={1.25}>
            <Button component={RouterLink} to="/sellers/valuation" variant="contained" fullWidth>
              Book a valuation
            </Button>
            <Button component={RouterLink} to="/repairs" variant="outlined" fullWidth>
              Report a repair
            </Button>
            <Button component={RouterLink} to="/contact" variant="text" fullWidth>
              Contact our office
            </Button>
          </Stack>
          <Stack spacing={0.75} sx={{ mt: 3, color: 'text.secondary' }}>
            <Link href="tel:+441642243378" color="inherit" underline="none">01642 243 378</Link>
            <Link href="mailto:info@mrmlettings.co.uk" color="inherit" underline="none">info@mrmlettings.co.uk</Link>
          </Stack>
        </Box>
      </Drawer>
    </Box>
  );
}

const utilityLinkSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.6,
  fontSize: '0.76rem',
  color: 'rgba(255,255,255,.86)',
  '&:hover': { color: '#fff' },
};
